"use client";

import { c as _c } from "react/compiler-runtime";
import { Clock, Building, CheckCircle, AlertCircle, Info } from "lucide-react";
type UnbStatus = "pending" | "submitted" | "approved" | "rejected";
interface UnbWaitingStatusProps {
  status?: UnbStatus;
  submittedAt?: string | null;
  approvedAt?: string | null;
  notes?: string | null;
  className?: string;
}
const STATUS_CONFIG: Record<UnbStatus, {
  icon: React.ComponentType<{
    className?: string;
  }>;
  bg: string;
  border: string;
  text: string;
  title: string;
  description: string;
}> = {
  pending: {
    icon: Clock,
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    title: "Vertrag unterschrieben – Einreichung wird vorbereitet",
    description: "Beide Parteien haben den Vertrag unterschrieben. Unser Team bereitet nun die Einreichung bei der Unteren Naturschutzbehörde (UNB) vor."
  },
  submitted: {
    icon: Building,
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    title: "Bei der UNB eingereicht",
    description: "Der Vertrag wurde bei der Unteren Naturschutzbehörde eingereicht und wird geprüft. Dies kann einige Wochen dauern."
  },
  approved: {
    icon: CheckCircle,
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    title: "Von UNB genehmigt",
    description: "Die Untere Naturschutzbehörde hat den Vertrag genehmigt."
  },
  rejected: {
    icon: AlertCircle,
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    title: "Von UNB abgelehnt",
    description: "Die Untere Naturschutzbehörde hat den Vertrag nicht genehmigt."
  }
};

/**
 * UnbWaitingStatus - Shows UNB approval status
 */
export function UnbWaitingStatus(t0) {
  const $ = _c(31);
  const {
    status: t1,
    submittedAt,
    approvedAt,
    notes,
    className: t2
  } = t0;
  const status = t1 === undefined ? "pending" : t1;
  const className = t2 === undefined ? "" : t2;
  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;
  const t3 = `rounded-xl border ${config.border} ${config.bg} p-6 ${className}`;
  const t4 = `w-14 h-14 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0 border ${config.border}`;
  const t5 = `w-7 h-7 ${config.text}`;
  let t6;
  if ($[0] !== StatusIcon || $[1] !== t5) {
    t6 = <StatusIcon className={t5} />;
    $[0] = StatusIcon;
    $[1] = t5;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== t4 || $[4] !== t6) {
    t7 = <div className={t4}>{t6}</div>;
    $[3] = t4;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = `text-lg font-semibold ${config.text}`;
  let t9;
  if ($[6] !== config.title || $[7] !== t8) {
    t9 = <h3 className={t8}>{config.title}</h3>;
    $[6] = config.title;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== config.description) {
    t10 = <p className="text-zinc-600 mt-1">{config.description}</p>;
    $[9] = config.description;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t10 || $[12] !== t9) {
    t11 = <div className="flex-1">{t9}{t10}</div>;
    $[11] = t10;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t11 || $[15] !== t7) {
    t12 = <div className="flex items-start gap-4">{t7}{t11}</div>;
    $[14] = t11;
    $[15] = t7;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== approvedAt || $[18] !== status || $[19] !== submittedAt) {
    t13 = (submittedAt || approvedAt) && <div className="mt-6 pl-2 border-l-2 border-zinc-200 space-y-4">{submittedAt && <div className="relative pl-6"><div className="absolute left-[-9px] top-1.5 w-4 h-4 rounded-full bg-blue-100 border-2 border-blue-500" /><p className="text-sm font-medium text-zinc-700">Eingereicht</p><p className="text-sm text-zinc-500">{new Date(submittedAt).toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "long",
            year: "numeric"
          })}</p></div>}{approvedAt && status === "approved" && <div className="relative pl-6"><div className="absolute left-[-9px] top-1.5 w-4 h-4 rounded-full bg-emerald-100 border-2 border-emerald-500" /><p className="text-sm font-medium text-zinc-700">Genehmigt</p><p className="text-sm text-zinc-500">{new Date(approvedAt).toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "long",
            year: "numeric"
          })}</p></div>}</div>;
    $[17] = approvedAt;
    $[18] = status;
    $[19] = submittedAt;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== notes) {
    t14 = notes && <div className="mt-6 p-4 bg-white/50 rounded-lg border border-zinc-200"><div className="flex items-start gap-2"><Info className="w-5 h-5 text-zinc-400 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-zinc-700">Hinweis</p><p className="text-sm text-zinc-600 mt-1">{notes}</p></div></div></div>;
    $[21] = notes;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== status) {
    t15 = (status === "pending" || status === "submitted") && <div className="mt-6 p-4 bg-white/80 rounded-lg border border-zinc-200"><h4 className="text-sm font-semibold text-zinc-800 mb-2">Was passiert bei der UNB-Prüfung?</h4><ul className="text-sm text-zinc-600 space-y-2"><li className="flex items-start gap-2"><span className="text-zinc-400 mt-0.5">•</span>Die Untere Naturschutzbehörde prüft die Übertragung der Ökopunkte</li><li className="flex items-start gap-2"><span className="text-zinc-400 mt-0.5">•</span>Die Bearbeitungszeit beträgt in der Regel 2-6 Wochen</li><li className="flex items-start gap-2"><span className="text-zinc-400 mt-0.5">•</span>Sie werden automatisch benachrichtigt, sobald eine Entscheidung vorliegt</li></ul></div>;
    $[23] = status;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t13 || $[27] !== t14 || $[28] !== t15 || $[29] !== t3) {
    t16 = <div className={t3}>{t12}{t13}{t14}{t15}</div>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t3;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
}